import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { HelpCircle } from 'lucide-react';
export function FAQPage() {
  useEffect(() => {
    // SEO meta tags
    document.title = 'Frequently Asked Questions | ShoppingExpert.ai';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Find answers to common questions about ShoppingExpert.ai, AI shopping assistants, and how to use our directory of verified AI agents.');
    }
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);
  const faqs = [{
    category: 'General',
    questions: [{
      q: 'What is ShoppingExpert.ai?',
      a: 'ShoppingExpert.ai is an independent directory of AI Shopping Agents & Sales Advisors. We curate, test, and verify 100+ AI tools across 20+ categories to help shoppers make smarter purchasing decisions.'
    }, {
      q: 'Is ShoppingExpert.ai free to use?',
      a: 'Yes! Browsing our directory and accessing most AI agents is completely free. Some individual AI tools may have their own pricing, which we clearly indicate on their listings.'
    }, {
      q: 'How often is the directory updated?',
      a: 'We update the directory weekly with new AI agents, feature updates, and evaluations. Our team continuously monitors the AI shopping landscape to keep our listings current.'
    }]
  }, {
    category: 'AI Agents',
    questions: [{
      q: 'Are AI shopping assistants accurate?',
      a: 'Yes — especially when they analyze specs, reviews, and pricing. Every agent on ShoppingExpert.ai is manually reviewed for accuracy, transparency, and reliability before being listed.'
    }, {
      q: 'Can AI find real coupons?',
      a: 'Yes! AI coupon advisors can discover codes from forums, merchant feeds, and hidden sources that traditional search often misses.'
    }, {
      q: 'Does it work outside the U.S.?',
      a: 'Many AI agents work internationally, though some are U.S.-focused. We indicate geographic availability on each listing. Import tax calculations may vary by region.'
    }, {
      q: 'Can I trust the recommendations?',
      a: 'Yes. We rank agents by performance, not payments. Our recommendations are based on data, testing, and user satisfaction signals — not sponsorships or affiliate commissions.'
    }]
  }, {
    category: 'Using the Directory',
    questions: [{
      q: 'How do I find the right AI agent for my needs?',
      a: 'Use our category filters to browse by shopping type (electronics, fashion, travel, etc.) or search for specific needs. Each listing includes detailed descriptions and use cases.'
    }, {
      q: 'What categories are available?',
      a: 'We cover 20+ categories including General AI Advisors, Shopping-Focused AI, Fashion & Styling, Travel & Flights, Groceries, Electronics, Beauty, Home & Kitchen, and more.'
    }, {
      q: 'Can I suggest an AI agent to be added?',
      a: "Absolutely! We welcome community submissions. Email us at info@shopilots.com with details about the AI agent you'd like us to review."
    }]
  }, {
    category: 'Review Process',
    questions: [{
      q: 'How do you evaluate AI agents?',
      a: "Every agent passes our strict review process evaluating: accuracy of recommendations, transparency & reliability, real product knowledge, consistency across categories, and user satisfaction signals. If it doesn't meet our standard, it doesn't get listed."
    }, {
      q: 'Are your reviews biased?',
      a: "No. We maintain complete independence. We don't rank agents by payments or sponsorships — only by performance and user value."
    }, {
      q: 'Can AI agents pay to be featured?',
      a: 'No. Our rankings and featured placements are based solely on quality, performance, and user benefit. We do not accept payment for rankings or reviews.'
    }]
  }, {
    category: 'Privacy & Data',
    questions: [{
      q: 'Do you collect my data?',
      a: "We use standard analytics (Google Analytics, Ahrefs) to understand how people use our site. We do not sell personal data. When you click through to AI agents, you're subject to their individual privacy policies."
    }, {
      q: 'Do I need to create an account?',
      a: 'No account is needed to browse our directory and access AI agents. You can start using our resources immediately.'
    }]
  }];
  return <div className="min-h-screen bg-slate-50" data-id="element-805">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-200" data-id="element-806">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4" data-id="element-807">
          <div className="flex items-center justify-between" data-id="element-808">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-809">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yJ3FscyPfrN1xAm7crLNW/ChatGPT_Image_Dec_2%2C_2025%2C_10_42_07_PM.png" alt="Shopping Expert.ai Logo" className="h-12 object-contain" data-id="element-810" />
              <span className="text-xl font-bold text-slate-900" data-id="element-811">
                AI Shopping Expert
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm" data-id="element-812">
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-813">
                Home
              </Link>
              <Link to="/research" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-814">
                Research Tools
              </Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-815">
                About
              </Link>
              <Link to="/faq" className="text-slate-900 font-semibold border-b-2 border-slate-900 pb-1" data-id="element-816">
                FAQ
              </Link>
              <Link to="/contact" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-817">
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-12" data-id="element-818">
        <Breadcrumbs items={[{
        label: 'FAQ',
        href: '/faq'
      }]} data-id="element-819" />

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12" data-id="element-820">
          <div className="text-center mb-12" data-id="element-821">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-4" data-id="element-822">
              <HelpCircle size={32} className="text-blue-600" data-id="element-823" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-824">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-slate-600" data-id="element-825">
              Find answers to common questions about ShoppingExpert.ai and AI
              shopping assistants
            </p>
          </div>

          {/* FAQ Categories */}
          {faqs.map((category, categoryIdx) => <div key={categoryIdx} className="mb-12 last:mb-0" data-id="element-826">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-slate-200" data-id="element-827">
                {category.category}
              </h2>
              <div className="space-y-6" data-id="element-828">
                {category.questions.map((faq, faqIdx) => <div key={faqIdx} className="p-6 rounded-xl bg-slate-50 border border-slate-200" data-id="element-829">
                    <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-start gap-2" data-id="element-830">
                      <span className="text-blue-600 flex-shrink-0" data-id="element-831">Q:</span>
                      <span data-id="element-832">{faq.q}</span>
                    </h3>
                    <p className="text-slate-700 leading-relaxed pl-6" data-id="element-833">
                      <span className="font-semibold text-green-600" data-id="element-834">A:</span>{' '}
                      {faq.a}
                    </p>
                  </div>)}
              </div>
            </div>)}

          {/* Still Have Questions? */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 text-center" data-id="element-835">
            <h2 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-836">
              Still Have Questions?
            </h2>
            <p className="text-slate-700 mb-6" data-id="element-837">
              Can't find what you're looking for? We're here to help!
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-black transition-colors" data-id="element-838">
              Contact Us
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16" data-id="element-839">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-id="element-840">
          {/* Our Review Method */}
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200" data-id="element-841">
            <h3 className="text-xl font-bold text-slate-900 mb-3 text-center" data-id="element-842">
              Our Review Method
            </h3>
            <p className="text-sm text-slate-700 mb-4 text-center" data-id="element-843">
              How We Evaluate AI Agents
            </p>
            <p className="text-sm text-slate-700 mb-4 text-center" data-id="element-844">
              Every agent on ShoppingExpert.ai passes a strict, Trustpilot-style
              review:
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-4" data-id="element-845">
              {['Accuracy of recommendations', 'Transparency & reliability', 'Real product knowledge', 'Consistency across categories', 'User satisfaction signals'].map((item, idx) => <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-slate-700 text-xs font-medium" data-id="element-846">
                  <span className="text-green-600 font-bold" data-id="element-847">✓</span>
                  {item}
                </span>)}
            </div>
            <p className="text-sm text-slate-900 font-bold text-center" data-id="element-848">
              If it doesn't meet our standard — it doesn't get listed.
            </p>
          </div>

          <div className="text-center mb-4" data-id="element-849">
            <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-850">
              ShoppingExpert.ai
            </p>
            <p className="text-xs text-slate-600" data-id="element-851">
              The independent directory of AI Shopping Agents & Sales Advisors.
            </p>
            <p className="text-xs text-slate-600" data-id="element-852">
              Updated weekly. Curated by ShoPilots.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500" data-id="element-853">
            <div data-id="element-854">© 2025 ShoppingExpert.ai • All rights reserved</div>
            <div className="flex items-center gap-3" data-id="element-855">
              <Link to="/about" className="underline" data-id="element-856">
                About
              </Link>
              <Link to="/faq" className="underline" data-id="element-857">
                FAQ
              </Link>
              <Link to="/contact" className="underline" data-id="element-858">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}